import './App.css';
import React, { Suspense, lazy, useEffect } from 'react';
import StartPage from './components/StartPage';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

// ScrollToTop — on every route change, reset scroll to the top. Fixes tabs/pages that
// loaded pre-scrolled (kept the previous page's scroll position). The app scrolls the
// window (outer container is min-height:100vh, so the body grows and the window scrolls),
// so window.scrollTo covers it; we also reset the document scrolling element defensively.
// NOTE: dashboards that switch "tabs" via internal state (not routes) additionally reset on
// their own view change — see the selectedView effect in FacultyDashboard.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [pathname]);
  return null;
}

// Lazy-load everything except the landing page to shrink the initial bundle.
// Was: single 322KB main.js with ~78% unused on first paint (Lighthouse 2026-04-07).
const LogIn = lazy(() => import('./components/LogIn'));
// SupervisorLoginPage removed: /login now handles passwordless flow when
// the password field is left empty. /supervisor/login redirects to /login.
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage'));
const DemoLogin = lazy(() => import('./components/DemoLogin'));
const DemoStudentDashboard = lazy(() => import('./components/demo/DemoStudentDashboard'));
const DemoManagerDashboard = lazy(() => import('./components/demo/DemoManagerDashboard'));
const DemoAdminDashboard = lazy(() => import('./components/demo/DemoAdminDashboard'));
const DemoLeaderboard = lazy(() => import('./components/demo/DemoLeaderboard'));
const DemoActivity2 = lazy(() => import('./components/demo/DemoActivity2'));
const DemoActivity17 = lazy(() => import('./components/demo/DemoActivity17'));
const StudentIntakeFlow = lazy(() => import('./components/demo/StudentIntakeFlow'));
// Marketing site. Lazy so the ~zero-JS-need landing page does not pull the
// dashboards into the initial bundle for a logged-in user, and vice versa.
const SiteHome = lazy(() => import('./site/pages/Home'));
const SitePlatform = lazy(() => import('./site/pages/Platform'));
const SiteOnePager = lazy(() => import('./site/pages/OnePager'));
const SiteAbout = lazy(() => import('./site/pages/About'));
const SiteDemo = lazy(() => import('./site/pages/Demo'));
const SiteCodeDetective = lazy(() => import('./site/pages/CodeDetective'));
const SitePrivacy = lazy(() => import('./site/pages/Privacy'));
const SiteNotFound = lazy(() => import('./site/pages/NotFound'));
const SetPasswordPage = lazy(() => import('./pages/SetPasswordPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const FacultyGateway = lazy(() => import('./pages/FacultyGateway'));
const CreateSectionFlow = lazy(() => import('./pages/CreateSectionFlow'));
const FacultyDashboard = lazy(() => import('./components/dashboards/FacultyDashboard'));
const IntakePage = lazy(() => import('./pages/IntakePage'));
const FacultyIntakePage = lazy(() => import('./pages/FacultyIntakePage'));
const ManagerIntakePage = lazy(() => import('./pages/ManagerIntakePage'));
const Activity2 = lazy(() => import('./components/activities/Activity2'));
const Activity14 = lazy(() => import('./components/activities/Activity14'));
const Activity18 = lazy(() => import('./components/activities/Activity18'));
const Activity19 = lazy(() => import('./components/activities/Activity19'));
const Activity20 = lazy(() => import('./components/activities/Activity20'));
const Activity21 = lazy(() => import('./components/activities/Activity21'));
const Activity22 = lazy(() => import('./components/activities/Activity22'));
const Activity23 = lazy(() => import('./components/activities/Activity23'));
const Activity24 = lazy(() => import('./components/activities/Activity24'));
const Activity25 = lazy(() => import('./components/activities/Activity25'));
const Activity26 = lazy(() => import('./components/activities/Activity26'));
const Activity27 = lazy(() => import('./components/activities/Activity27'));
const Activity28 = lazy(() => import('./components/activities/Activity28'));
const Activity29 = lazy(() => import('./components/activities/Activity29'));
const Activity30 = lazy(() => import('./components/activities/Activity30'));
const Activity31 = lazy(() => import('./components/activities/Activity31'));
const Activity32 = lazy(() => import('./components/activities/Activity32'));
const Activity33 = lazy(() => import('./components/activities/Activity33'));
const Activity34 = lazy(() => import('./components/activities/Activity34'));
const Activity35 = lazy(() => import('./components/activities/Activity35'));
const Activity36 = lazy(() => import('./components/activities/Activity36'));
const Activity37 = lazy(() => import('./components/activities/Activity37'));
const TimeCapsule = lazy(() => import('./components/dashboard-content-pages/TimeCapsule'));
const CUNY2026 = lazy(() => import('./pages/CUNY2026'));
const Baruch2026 = lazy(() => import('./pages/Baruch2026'));
const ColumbiaDemo = lazy(() => import('./pages/ColumbiaDemo'));
const UIPreview = lazy(() => import('./pages/UIPreview'));
const Launch = lazy(() => import('./pages/Launch'));
const SuperMomos = lazy(() => import('./pages/SuperMomos'));
const DisabledPage = lazy(() => import('./pages/DisabledPage'));

function App() {
  return (
    <div className="App">
      <Router>
        <ScrollToTop />
        <ChunkErrorBoundary>
        <Suspense fallback={<div style={{ minHeight: '100vh', background: '#200043', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Sora, sans-serif', color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>Loading…</div>}>
          <Routes>

            {/* ROOT stays the existing app landing page. The marketing site is
                NOT live at / yet, by request. It is mounted under /preview so it
                can be reviewed without changing what a real visitor sees.

                TO GO LIVE: set SITE_BASE = '' in site/content/global.js AND make
                this route <SiteHome />. Those two changes together, nothing else. */}
            <Route
              exact
              path="/"
              element={<StartPage />}
            />

            {/* Marketing site, under /preview. Every page is built to survive
                being forwarded as a bare link. */}
            <Route path='/preview' element={<SiteHome />} />
            <Route path='/preview/platform' element={<SitePlatform />} />
            <Route path='/preview/about' element={<SiteAbout />} />
            <Route path='/preview/demo' element={<SiteDemo />} />
            {/* The activity has its own route and its own nav entry. It was
                previously reachable only via a nav item labelled "Demo". */}
            <Route path='/preview/code-detective' element={<SiteCodeDetective />} />
            <Route path='/preview/privacy' element={<SitePrivacy />} />
            {/* Forwardable artifact. Unlinked from the nav; Clara sends the URL. */}
            <Route path='/preview/one-pager' element={<SiteOnePager />} />

            {/* Retired site routes, redirecting inside the preview. */}
            <Route path='/preview/pricing' element={<Navigate to='/preview/platform#included' replace />} />
            <Route path='/preview/semester' element={<Navigate to='/preview/platform#placement' replace />} />
            <Route path='/preview/security' element={<Navigate to='/preview/platform#security' replace />} />
            <Route path='/preview/try' element={<Navigate to='/preview/code-detective' replace />} />
            <Route path='/preview/try-it' element={<Navigate to='/preview/code-detective' replace />} />

            {/* /login STAYS as the real login.
                The build brief calls for redirecting it to app.careerstar.co,
                but that subdomain is not serving the app yet and this route is
                what every live customer and every outstanding magic-link email
                lands on. Redirecting it now would lock users out on deploy.
                TODO(cutover): once app.careerstar.co resolves, swap this for
                a redirect AND flip APP_URL / LOGIN_IS_EXTERNAL in
                site/content/global.js — all three together. */}
            <Route
              exact
              path='/login'
              element={<LogIn />}
            />

            {/* /supervisor/login is the legacy URL — kept alive via redirect so
                any prior email links / bookmarks still route the supervisor to
                the consolidated single /login page (which now handles the
                passwordless flow when the password field is left empty). */}
            <Route
              exact
              path='/supervisor/login'
              element={<Navigate to="/login" replace />}
            />

            {/* Demo routes — DISABLED 2026-06-24 (no longer used). Components kept; routes off.
            <Route exact path='/demo/login' element={<DemoLogin />} />
            <Route exact path='/demo/student' element={<DemoStudentDashboard />} />
            <Route exact path='/demo/manager' element={<DemoManagerDashboard />} />
            <Route exact path='/demo/administrator' element={<DemoAdminDashboard />} />
            <Route exact path='/demo/leaderboard' element={<DemoLeaderboard />} />
            <Route exact path='/demo/activity/2' element={<DemoActivity2 />} />
            <Route exact path='/demo/activity/17' element={<DemoActivity17 />} />
            <Route exact path='/demo/student/intake' element={<StudentIntakeFlow />} />
            */}
            {/* Old demo is retired — anything under /demo shows the friendly disabled page. */}
            <Route path='/demo/*' element={<DisabledPage />} />

            <Route
              path='/reset-password/:token'
              element={<ResetPasswordPage />}
            />

            {/* Public flyer page */}
            <Route path='/CUNY2026' element={<CUNY2026 />} />
            {/* Both casings: the flyer link gets typed lowercase and React Router is
                case-sensitive, so /baruch2026 would otherwise 404. */}
            <Route path='/baruch2026' element={<Baruch2026 />} />
            <Route path='/Baruch2026' element={<Baruch2026 />} />
            {/* Public, no-login sales demo — Columbia Strategic Communication Capstone.
                Public Columbia capstone demo — deliberately NOT under /demo (that path is retired). */}
            <Route path='/columbiacapstone' element={<ColumbiaDemo />} />
            {/* UI primitives fidelity preview (unlinked, dev-only) */}
            <Route path='/ui-preview' element={<UIPreview />} />
            {/* Investor brief — unlinked, not in public nav */}
            <Route path='/launch' element={<Launch />} />
            {/* <Route path='/supermomos' element={<SuperMomos />} />  DISABLED 2026-06-24 — customer never returned */}
            <Route path='/supermomos' element={<DisabledPage />} />

            {/* Student self-signup */}
            <Route path='/signup' element={<SignupPage />} />

            {/* Magic link password setup */}
            <Route path='/set-password' element={<SetPasswordPage />} />

            {/* Production activity routes */}
            <Route exact path='/dashboard/activity/2' element={<Activity2 />} />
            <Route exact path='/dashboard/activity/14' element={<Activity14 />} />
            <Route exact path='/dashboard/activity/18' element={<Activity18 />} />
            <Route exact path='/dashboard/activity/19' element={<Activity19 />} />
            <Route exact path='/dashboard/activity/20' element={<Activity20 />} />
            <Route exact path='/dashboard/activity/21' element={<Activity21 />} />
            <Route exact path='/dashboard/activity/22' element={<Activity22 />} />
            <Route exact path='/dashboard/activity/23' element={<Activity23 />} />
            <Route exact path='/dashboard/activity/24' element={<Activity24 />} />
            <Route exact path='/dashboard/activity/25' element={<Activity25 />} />
            <Route exact path='/dashboard/activity/26' element={<Activity26 />} />
            <Route exact path='/dashboard/activity/27' element={<Activity27 />} />
            <Route exact path='/dashboard/activity/28' element={<Activity28 />} />
            <Route exact path='/dashboard/activity/29' element={<Activity29 />} />
            <Route exact path='/dashboard/activity/30' element={<Activity30 />} />
            <Route exact path='/dashboard/activity/31' element={<Activity31 />} />
            <Route exact path='/dashboard/activity/32' element={<Activity32 />} />
            <Route exact path='/dashboard/activity/33' element={<Activity33 />} />
            <Route exact path='/dashboard/activity/34' element={<Activity34 />} />
            <Route exact path='/dashboard/activity/35' element={<Activity35 />} />
            <Route exact path='/dashboard/activity/36' element={<Activity36 />} />
            <Route exact path='/dashboard/activity/37' element={<Activity37 />} />
            <Route exact path='/dashboard/time-capsule' element={<TimeCapsule />} />

            {/* Production routes (role-based) */}
            <Route path='/home' element={<HomePage />} />

            {/* Grade notifications (past and present) link to /dashboard, which has no route and
                blanks the app. /dashboard is grade-only, so send it straight to the student's
                Gradebook. New notifications link to /home?view=assignments&tab=gradebook directly. */}
            <Route path='/dashboard' element={<Navigate to='/home?view=assignments&tab=gradebook' replace />} />

            {/* Faculty redesign: gateway landing -> pick a section -> section home;
                "+ Add" opens the create-section flow. Section home is FacultyDashboard
                scoped to :sectionId (it delegates to mobile internally). */}
            <Route path='/faculty' element={<FacultyGateway />} />
            <Route path='/faculty/new-section' element={<CreateSectionFlow />} />
            <Route path='/faculty/section/:sectionId' element={<FacultyDashboard />} />
            <Route path='/intake' element={<IntakePage />} />
            <Route path='/faculty-intake' element={<FacultyIntakePage />} />
            <Route path='/manager-intake' element={<ManagerIntakePage />} />

            {/* Catch-all. Previously an unmatched path rendered a BLANK page —
                the router had no "*" route at all. The marketing nav links to
                pages that ship in later passes, so this is required now, but it
                is an improvement for mistyped app URLs either way. */}
            <Route path='*' element={<SiteNotFound />} />
          </Routes>
        </Suspense>
        </ChunkErrorBoundary>
      </Router>
    </div>
  );
}

export default App;
